import { useEffect, useRef, useState } from 'react';
import { encodeWav } from './audio.ts';

export function useRecorder({
  onSpeechStart,
  onComplete,
}: {
  onSpeechStart: () => void;
  onComplete: (wav: Blob, signal: AbortSignal) => Promise<void>;
}) {
  const [status, setStatus] = useState<
    'idle' | 'requesting' | 'listening' | 'recording' | 'transcribing'
  >('idle');
  const [error, setError] = useState(''),
    [level, setLevel] = useState(0);
  const generation = useRef(0);
  const transcription = useRef<AbortController | undefined>(undefined);
  const refs = useRef<{
    context?: AudioContext;
    stream?: MediaStream;
    node?: AudioWorkletNode;
    chunks: Float32Array[];
    timer?: ReturnType<typeof setTimeout>;
    active: boolean;
    speech: boolean;
    above: number;
    quiet: number;
    rate: number;
  }>({
    chunks: [],
    active: false,
    speech: false,
    above: 0,
    quiet: 0,
    rate: 48000,
  });
  const callbacks = useRef({ onSpeechStart, onComplete });
  callbacks.current = { onSpeechStart, onComplete };
  function cleanup() {
    const r = refs.current;
    r.active = false;
    clearTimeout(r.timer);
    r.stream?.getTracks().forEach((t) => t.stop());
    r.node?.disconnect();
    if (r.context && r.context.state !== 'closed') void r.context.close();
    r.context = undefined;
    r.stream = undefined;
    r.node = undefined;
    setLevel(0);
  }
  async function stop(submit = true) {
    if (!submit) transcription.current?.abort();
    const r = refs.current;
    if (!r.active) {
      // Stop must cancel a pending permission/worklet request too. Otherwise
      // a later permission result can turn the mic on after the student stops.
      generation.current++;
      cleanup();
      setStatus('idle');
      return;
    }
    const chunks = r.chunks,
      rate = r.rate,
      speech = r.speech;
    cleanup();
    const stoppedGeneration = ++generation.current;
    r.chunks = [];
    if (!submit) {
      setStatus('idle');
      return;
    }
    if (!speech) {
      setError(
        'No clear speech was detected. Try again or type your question.',
      );
      setStatus('idle');
      return;
    }
    setStatus('transcribing');
    const controller = new AbortController();
    transcription.current = controller;
    try {
      await callbacks.current.onComplete(
        encodeWav(chunks, rate),
        controller.signal,
      );
    } catch (err) {
      if (
        !controller.signal.aborted &&
        stoppedGeneration === generation.current
      )
        setError(
          err instanceof Error
            ? err.message
            : 'Transcription failed. You can type instead.',
        );
    } finally {
      if (stoppedGeneration === generation.current) setStatus('idle');
      if (transcription.current === controller)
        transcription.current = undefined;
    }
  }
  async function start() {
    if (
      refs.current.active ||
      status === 'transcribing' ||
      status === 'requesting'
    )
      return;
    setError('');
    setStatus('requesting');
    const requestedGeneration = ++generation.current;
    let ownedStream: MediaStream | undefined;
    let ownedContext: AudioContext | undefined;
    const releaseOwned = () => {
      ownedStream?.getTracks().forEach((track) => track.stop());
      if (ownedContext && ownedContext.state !== 'closed')
        void ownedContext.close();
    };
    try {
      if (!navigator.mediaDevices?.getUserMedia)
        throw new Error('This browser cannot record here. Use typed input.');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      ownedStream = stream;
      if (requestedGeneration !== generation.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const context = new AudioContext();
      ownedContext = context;
      refs.current.stream = stream;
      refs.current.context = context;
      await context.audioWorklet.addModule('/recorder-worklet.js');
      await context.resume();
      if (requestedGeneration !== generation.current) {
        releaseOwned();
        return;
      }
      const node = new AudioWorkletNode(context, 'enactry-recorder');
      const mute = context.createGain();
      mute.gain.value = 0;
      context.createMediaStreamSource(stream).connect(node);
      node.connect(mute).connect(context.destination);
      const r = refs.current;
      Object.assign(r, {
        context,
        stream,
        node,
        chunks: [],
        active: true,
        speech: false,
        above: 0,
        quiet: 0,
        rate: context.sampleRate,
      });
      setStatus('listening');
      node.port.onmessage = ({ data }: MessageEvent<Float32Array>) => {
        if (!r.active || requestedGeneration !== generation.current) return;
        const rms = Math.sqrt(
          data.reduce((sum, n) => sum + n * n, 0) / data.length,
        );
        setLevel(Math.min(1, rms * 14));
        if (rms > 0.022) {
          r.above++;
          r.quiet = 0;
        } else {
          r.above = 0;
          if (r.speech) r.quiet += data.length / r.rate;
        }
        if (!r.speech && r.above >= 3) {
          r.speech = true;
          setStatus('recording');
          callbacks.current.onSpeechStart();
        }
        // Keep at most ~0.4 seconds of pre-roll while waiting for a question.
        r.chunks.push(data);
        if (
          !r.speech &&
          r.chunks.length > Math.ceil((r.rate * 0.4) / data.length)
        )
          r.chunks.shift();
        if (r.speech && r.quiet > 1.35) void stop();
      };
      r.timer = setTimeout(() => void stop(), 20000);
    } catch (err) {
      releaseOwned();
      if (requestedGeneration !== generation.current) return;
      cleanup();
      setStatus('idle');
      setError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Microphone permission was not granted. Typed input still works.'
          : err instanceof Error
            ? err.message
            : 'Microphone unavailable. You can type instead.',
      );
    }
  }
  useEffect(
    () => () => {
      generation.current++;
      transcription.current?.abort();
      const r = refs.current;
      r.active = false;
      clearTimeout(r.timer);
      r.stream?.getTracks().forEach((t) => t.stop());
      if (r.context && r.context.state !== 'closed') void r.context.close();
    },
    [],
  );
  return { status, error, level, start, stop, cancel: () => stop(false) };
}
